import { logger } from '../helpers/logger.helper'

/**
 * Drop-in replacement for `@supabase/supabase-js`'s exported `processLock`.
 *
 * gotrue's `processLock` `console.warn`s on EVERY contended acquire before it
 * rejects. The auto-refresh tick calls it with `acquireTimeout === 0`, so any
 * moment another auth op (getSession/refreshSession/setSession, visibility/
 * online handlers, dev HMR re-runs) briefly holds the lock, the console floods
 * with identical "Lock ... acquisition timed out after 0ms" warnings. That case
 * is benign — the tick catches the timeout and simply skips this round.
 *
 * This re-implements the exact `PROCESS_LOCKS` chaining (so auth operations stay
 * serialized — do NOT swap in a no-op lock, that reintroduces refresh races) and
 * changes the logging + fixes a latent timer leak:
 *   - acquireTimeout === 0  → benign tick backoff, stay silent.
 *   - acquireTimeout  >  0  → a prior op held the lock for the full timeout
 *     (e.g. a getSession/refresh stalling behind a throttled corporate NAT).
 *     That is a real signal, so route it through the project logger instead of
 *     dropping it.
 *
 * Crucially, `Promise.race` does NOT cancel its losers: gotrue's original
 * `processLock` never clears the acquire-timeout `setTimeout`, so the timer fires
 * `acquireTimeout` ms later for EVERY acquire — even one that won the lock in a
 * few ms. With the upstream `console.warn` (and our `logger.warn`) inside that
 * timer, every getSession / onAuthStateChange / setSession spuriously logs
 * "not acquired within 10000ms" ~10s after it already succeeded. We clear the
 * timer the instant the lock is acquired, so the warn fires ONLY on genuine
 * contention (the prior op truly held the lock past the timeout).
 *
 * The rejected error keeps `isAcquireTimeout = true` so gotrue's tick `catch`
 * (GoTrueClient `_autoRefreshTokenTick`) still recognizes it as backoff rather
 * than surfacing it as a hard error.
 */

class QuietProcessLockAcquireTimeoutError extends Error {
  public readonly isAcquireTimeout = true

  constructor(message: string) {
    super(message)
    this.name = 'QuietProcessLockAcquireTimeoutError'
  }
}

type LockFn<R> = () => Promise<R>

const PROCESS_LOCKS: Record<string, Promise<unknown>> = {}

export async function quietProcessLock<R>(
  name: string,
  acquireTimeout: number,
  fn: LockFn<R>,
): Promise<R> {
  const previousOperation = PROCESS_LOCKS[name] ?? Promise.resolve()

  // Hoisted so the `.then` (lock-acquired) path can cancel the pending timer.
  // Without this, the timer fires `acquireTimeout` ms after every acquire and
  // logs a false "not acquired" warning even when the lock was won instantly.
  let acquireTimer: ReturnType<typeof setTimeout> | undefined

  const currentOperation = Promise.race(
    [
      previousOperation.catch(() => {
        // ignore error of previous operation that we're waiting to finish
        return null
      }),
      acquireTimeout >= 0
        ? new Promise((_, reject) => {
            acquireTimer = setTimeout(() => {
              // Only a positive timeout means a real, prolonged hold worth a log.
              if (acquireTimeout > 0) {
                logger.warn(
                  `Supabase auth lock "${name}" not acquired within ${acquireTimeout}ms ` +
                    '(a prior auth op held it — likely a slow getSession/refresh under network throttling)',
                )
              }
              reject(
                new QuietProcessLockAcquireTimeoutError(
                  `Acquiring process lock with name "${name}" timed out`,
                ),
              )
            }, acquireTimeout)
          })
        : null,
    ].filter((x): x is Promise<unknown> => x != null),
  )
    .catch((e: unknown) => {
      if (e && (e as { isAcquireTimeout?: boolean }).isAcquireTimeout) {
        throw e
      }
      return null
    })
    .then(async () => {
      // previous operations finished and we didn't get a race on the acquire
      // timeout, so the current operation can finally start. We won the race, so
      // cancel the still-pending timer — otherwise it would later fire its warn
      // + (ignored) reject even though the lock was acquired successfully.
      if (acquireTimer !== undefined) clearTimeout(acquireTimer)
      return await fn()
    })

  PROCESS_LOCKS[name] = currentOperation.catch(async (e: unknown) => {
    if (e && (e as { isAcquireTimeout?: boolean }).isAcquireTimeout) {
      // if the current operation timed out, it doesn't mean that the previous
      // operation finished, so we need to continue waiting for it to finish
      await previousOperation
      return null
    }
    throw e
  })

  return await currentOperation
}
